import { useFetch } from "@4uhub/lib4uhub";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import { Box, Typography } from "@mui/material";
import { memo, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import useDownload from "../../../../../hooks/useDownload";
import {
  getLaboratorialExamsResultLabPdf,
  getLaboratorialExamsResultLabPdfAsTemporary,
} from "../../../../../services/laboratorialExams.service";
import {
  getNonLaboratorialExamsPdfSerial,
  getNonLaboratorialExamsPdfSerialTasy,
} from "../../../../../services/nonLaboratorialExams.service";
import { tokenTemporaryService } from "../../../../../services/tokenTemorary.service";
import { Actions } from "./Actions/Actions";
import { IFileItemProps } from "./models";

const translationPath = "components.reportsModal.";

export const FileItem = memo(
  ({
    index,
    identifier,
    isActive,
    cdnUrl,
    selectedFile,
    type,
    onSelectFile,
    onFileLoading,
    onGetFileIndex,
  }: IFileItemProps) => {
    const { t } = useTranslation();

    const isTemporaryAccess = !!tokenTemporaryService.getTemporaryAccessToken();

    const { sendRequest: getLaboratorialExam, loading: labExamLoading } =
      useFetch(
        isTemporaryAccess
          ? getLaboratorialExamsResultLabPdfAsTemporary
          : getLaboratorialExamsResultLabPdf,
      );

    const { sendRequest: getNonLaboratorialExam, loading: nonLabExamLoading } =
      useFetch(
        isTemporaryAccess
          ? getNonLaboratorialExamsPdfSerialTasy
          : getNonLaboratorialExamsPdfSerial,
      );

    const fetchLaboratorialExam = useCallback(async () => {
      const { data, success } = await getLaboratorialExam(identifier);
      if (data && success) {
        onSelectFile(data);
        onGetFileIndex(index);
      } else {
        onSelectFile(null);
      }
    }, [identifier, index, onGetFileIndex, onSelectFile, getLaboratorialExam]);

    const fetchNonLaboratorialExam = useCallback(async () => {
      const { data, success } = await getNonLaboratorialExam(identifier);
      if (data && success) {
        onSelectFile(data);
        onGetFileIndex(index);
      } else {
        onSelectFile(null);
      }
    }, [
      identifier,
      index,
      getNonLaboratorialExam,
      onSelectFile,
      onGetFileIndex,
    ]);

    const selectFileHandler = useCallback(() => {
      onGetFileIndex(index);
    }, [onGetFileIndex, index]);

    const { downloadBase64File } = useDownload();

    const handleDownloadFile = useCallback(() => {
      if (!selectedFile) return;

      if (selectedFile instanceof ArrayBuffer) {
        const blob = new Blob([selectedFile], { type: "application/pdf" });
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = `${t(`${translationPath}file`)} ${index + 1}.pdf`;
        link.click();
        URL.revokeObjectURL(blobUrl);
      } else {
        downloadBase64File(
          selectedFile,
          `${t(`${translationPath}file`)} ${index + 1}`,
        );
      }
    }, [index, selectedFile, downloadBase64File, t]);

    useEffect(() => {
      onFileLoading(labExamLoading);
    }, [onFileLoading, labExamLoading]);

    useEffect(() => {
      if (isActive) {
        if (type === "4") {
          fetchNonLaboratorialExam();
        } else {
          fetchLaboratorialExam();
        }
      }
    }, [fetchLaboratorialExam, fetchNonLaboratorialExam, isActive, type]);

    const isLoading = labExamLoading || nonLabExamLoading;

    return (
      <Box
        sx={(t) => ({
          p: 1,
          height: "fit-content",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderRadius: 15,
          cursor: isActive ? undefined : "pointer",
          transition: "ease-in-out 0.2s",
          "&:hover": isActive
            ? undefined
            : {
                backgroundColor:
                  t.palette.grey[t.palette.mode === "light" ? 100 : 900],
                borderRadius: 2,
              },
        })}
        onClick={isActive ? undefined : selectFileHandler}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={(t) => ({
              p: 0.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: isActive
                ? t.palette.primary.main
                : "transparent",
              color: isActive ? t.palette.primary.contrastText : "inherit",
              borderRadius: 15,
            })}
          >
            <AttachFileRoundedIcon fontSize="small" />
          </Box>
          <Typography>
            {t(`${translationPath}file`)} {index + 1}
          </Typography>
        </Box>
        <Actions
          viewHandler={selectFileHandler}
          funcHandler={
            type === "4" ? fetchNonLaboratorialExam : fetchLaboratorialExam
          }
          handleDownloadFile={handleDownloadFile}
          isLoading={isLoading}
          isActive={isActive}
          selectedFile={selectedFile}
          cdnUrl={cdnUrl}
          showViewButton={false}
          showDownloadButton={false}
        />
      </Box>
    );
  },
);
